import { useMemo, useState } from "react";
import type { CatalogEntry, ConnectorBubble } from "../lib/types";
import { searchCatalog } from "../lib/catalogSearch";

export function CatalogPicker({
  entries,
  bubbles,
  selected,
  onSelect,
  onRequest,
  onManage,
}: {
  entries: CatalogEntry[];
  bubbles: ConnectorBubble[];
  selected: string[];
  onSelect: (entryId: string) => void;
  onRequest: (entryId: string) => void;
  onManage?: (entryId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<"all" | "configured" | "requested" | "unavailable">("all");
  const bubbleMap = useMemo(() => new Map(bubbles.map((bubble) => [bubble.entry_id, bubble])), [bubbles]);
  const results = useMemo(() => {
    const matched = searchCatalog(entries, query, 200);
    return matched.filter((entry) => {
      const bubble = bubbleMap.get(entry.entry_id);
      if (filter === "configured") return bubble?.state === "green";
      if (filter === "requested") return bubble?.state === "amber";
      if (filter === "unavailable") return bubble?.state === "grey_muted" || bubble?.state === "red";
      return true;
    });
  }, [entries, query, filter, bubbleMap]);

  return (
    <section aria-label="Connector and website catalog" className="space-y-3">
      <input
        type="search"
        aria-label="Search connectors and websites"
        placeholder="Search by name, alias or paste a URL"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        className="w-full rounded-lg border border-[var(--tj-border)] bg-[var(--tj-surface)] px-3 py-2"
      />
      <div className="flex flex-wrap gap-2 text-xs">
        {(["all", "configured", "requested", "unavailable"] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={`rounded-full border px-3 py-1 ${filter === value ? "border-[var(--tj-accent)]" : "border-[var(--tj-border)]"}`}
          >
            {value}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {results.map((entry) => {
          const bubble = bubbleMap.get(entry.entry_id);
          const isSelected = selected.includes(entry.entry_id);
          const muted = bubble?.state === "grey_muted";
          const action: "select" | "request" | "manage" = !bubble
            ? "select"
            : bubble.state === "green"
              ? "select"
              : bubble.state === "amber"
                ? "request"
                : bubble.requestable
                  ? "request"
                  : "manage";
          // A broken connector is never "Selected": managing it is the only useful action.
          const label = muted
            ? "Unavailable"
            : action === "manage"
              ? "Manage"
              : isSelected
                ? "Selected"
                : action === "request"
                  ? "Request"
                  : "Add";
          return (
            <article key={entry.entry_id} className="flex items-start justify-between gap-2 rounded-lg border border-[var(--tj-border)] p-3">
              <div>
                <p className="font-medium">{entry.display_name}</p>
                <p className="text-xs text-[var(--tj-muted)]">
                  {entry.kind} · {entry.category} · {entry.tier}
                </p>
                {bubble ? (
                  <p className={`mt-1 text-xs tj-bubble-${bubble.state}`} title={bubble.reason}>
                    {bubble.state.replace("_", " ")}
                    {bubble.stage ? ` · ${bubble.stage}` : ""}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                disabled={muted}
                onClick={() => {
                  if (action === "request") onRequest(entry.entry_id);
                  else if (action === "manage") onManage?.(entry.entry_id);
                  else onSelect(entry.entry_id);
                }}
                className="rounded-md border border-[var(--tj-border)] px-2 py-1 text-xs disabled:opacity-40"
              >
                {label}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}
